import _ from 'lodash';
import { useMemo } from 'react';
import { useAppSelector } from '../../../app/hooks';
import {
  BlueberryFeatures,
  CubeDimensionEnum,
  FeatureEnum,
} from '../../../services/data/impl/cube/cubeSlice';
import {
  findHighestModelVersion,
  isBlueberryFeature,
} from '../../../services/data/impl/cube/cubeUtils';
import useCurrServicesAndMeasures from '../../customerSummary/hooks/useCurrServicesAndMeasures';
import {
  selectColumnsExistInDbData,
  selectScanDataTableExists,
} from '../../duckDB/duckDBSlice';
import { DuckQueryReturnListType } from '../../duckDB/hooks/useDuckQueryScanData';
import { selectCurrFeature } from '../../select/selectSlice';
import { ScanDataEnum } from '../scanDataDefinitions';
import { useBlueberryFlowerFeatures } from './useBlueberryFlowerFeatures';
import { OperatorEnum, useScanDataQuery } from './useScanDataQuery';

// This structure will be returned from duck db
interface BlueberryQueryData {
  [ScanDataEnum.class_name]: string;
  sum_estimated_count: number;
  sum_distance_traveled: number;
}

export interface BlueberryDataPerClass {
  block_avg: number | null;
}

// This structure will be used to group
// metrics by feature
export interface BlueberryData {
  [FeatureEnum.blueberry_Blue]: BlueberryDataPerClass;
  [FeatureEnum.blueberry_Green]: BlueberryDataPerClass;
  [FeatureEnum.blueberry_Purple]: BlueberryDataPerClass;
  [FeatureEnum.blueberry_Yellow]: BlueberryDataPerClass;
}

export const useBlueberryBlockAverage =
  (): DuckQueryReturnListType<BlueberryData> => {
    const currFeature = useAppSelector(selectCurrFeature);
    const scanDataTableExists = useAppSelector(selectScanDataTableExists);
    const { servicesAndMeasuresWithFeatureFilter } =
      useCurrServicesAndMeasures();
    const columnsExistInDbData = useAppSelector(selectColumnsExistInDbData);
    const flowerFeatures = useBlueberryFlowerFeatures();

    const isBlueberry = useMemo(
      () => isBlueberryFeature(currFeature),
      [currFeature]
    );

    // in blueberry case we need only model version and model name
    // model version will be always synced between classes
    const latestService = findHighestModelVersion(
      servicesAndMeasuresWithFeatureFilter.availableServicesRaw,
      [...BlueberryFeatures, ...flowerFeatures]
    );

    const [cols, filters, group] = useMemo(() => {
      // filter out columns that non existing in db
      if (!columnsExistInDbData?.data?.[ScanDataEnum.traveled_distance]) {
        return [[], undefined, undefined];
      }

      const cols = [
        ScanDataEnum.class_name,
        `SUM(${ScanDataEnum.estimated_count}) as sum_estimated_count`,
        `SUM(${ScanDataEnum.traveled_distance}) as sum_distance_traveled`,
      ];
      const filters = [];

      if (latestService != null) {
        filters.push(
          {
            col: ScanDataEnum.model_name,
            op: OperatorEnum.eq,
            value: latestService[CubeDimensionEnum.modelName],
          },
          {
            col: ScanDataEnum.model_version,
            op: OperatorEnum.eq,
            value: latestService[CubeDimensionEnum.modelVersion],
          },
          {
            col: ScanDataEnum.class_name,
            op: OperatorEnum.in,
            value: [...BlueberryFeatures, ...flowerFeatures],
          }
        );
      }

      const group = [ScanDataEnum.class_name];
      return [cols, filters, group];
    }, [latestService, columnsExistInDbData?.data, flowerFeatures]);

    // at first this query order every row by class name
    // then it makes grouping by point
    // and merge rows that has counts into single row (array agg)
    const blueberryQueryResult = useScanDataQuery<BlueberryQueryData>({
      cols,
      group,
      filters,
      skip:
        !latestService ||
        servicesAndMeasuresWithFeatureFilter.availableServicesRaw.length ===
          0 ||
        !isBlueberry ||
        !scanDataTableExists ||
        !columnsExistInDbData?.data?.[ScanDataEnum.traveled_distance],
      disableClassFilter: true,
      useUnitsTransformSubstringMatching: true,
    });

    const postProcessedResult = useMemo(() => {
      if (!blueberryQueryResult.data) {
        return null;
      }
      const groupedData = _.groupBy(
        blueberryQueryResult.data,
        ScanDataEnum.point_id
      );
      const pointsWithFeatureBasedData = Object.keys(groupedData).map(
        (pointId) => {
          const pointRelatedData = groupedData[pointId];
          const pointBase = _.omit(pointRelatedData[0], [
            'sum_estimated_count',
            'sum_distance_traveled',
          ]);
          const classRelatedData = Object.fromEntries(
            Object.entries(
              _.groupBy(pointRelatedData, ScanDataEnum.class_name)
            ).map(([key, value]) => [
              key,
              {
                block_avg:
                  value[0].sum_estimated_count / value[0].sum_distance_traveled,
              },
            ])
          ) as {
            [FeatureEnum.blueberry_Blue]: BlueberryDataPerClass;
            [FeatureEnum.blueberry_Green]: BlueberryDataPerClass;
            [FeatureEnum.blueberry_Purple]: BlueberryDataPerClass;
            [FeatureEnum.blueberry_Yellow]: BlueberryDataPerClass;
            [FeatureEnum.flower]: BlueberryDataPerClass;
            [FeatureEnum.flower_closed]: BlueberryDataPerClass;
            [FeatureEnum.flower_open]: BlueberryDataPerClass;
            [FeatureEnum.flower_petalfall]: BlueberryDataPerClass;
          };

          return {
            ...pointBase,
            ...classRelatedData,
          } as BlueberryData;
        }
      );

      return pointsWithFeatureBasedData;
    }, [blueberryQueryResult.data]);

    return {
      ...blueberryQueryResult,
      data: postProcessedResult,
      error: !columnsExistInDbData?.data?.[ScanDataEnum.traveled_distance]
        ? new Error('No traveled distance data')
        : blueberryQueryResult.error,
    };
  };
